import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { createAppError } from "@@/shared/errors/application-error";
import { idZodSchema } from "@@/shared/id";
import { imageReferenceZodSchema } from "@@/shared/images/image-reference";
import {
    defaultAnonymizedPhoneNumber,
    phoneNumberZodSchema,
} from "@@/shared/phone";
import { customerIdZodSchema } from "@@/shared/users/customer-id";
import { type User, isUser } from "@@/shared/users/user";
import { isUserId, userIdZodSchema } from "@@/shared/users/user-id";
import { fullName } from "@@/utilities/full-name";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";
import type { Customer } from "../users/customer";
import { isCustomer } from "../users/is-customer";

const contactInfoZodObject = z.object({
    _id: idZodSchema,
    _type: z.literal("CONTACT_INFO"),
    /** Not available on contact info model pre 2024-04-22 */
    _createdAt: isoAndUnixZodSchema.nullish(),
    name: z.string().trim().nullish(),
    firstName: z.string().trim().nullish(),
    lastName: z.string().trim().nullish(),
    emailAddress: z.string().trim().email().or(z.literal("")).nullish(),
    phoneNumber: phoneNumberZodSchema,
    image: imageReferenceZodSchema.nullish(),
    businessName: z.string().trim().nullish(),
    businessOrgNo: z.string().trim().nullish(),
    businessVatNo: z.string().trim().nullish(),
    customerId: userIdZodSchema.or(customerIdZodSchema).nullish(),
});

type ContactInfo = z.infer<typeof contactInfoZodObject>;

const contactInfoFactoryParamsZodObject = contactInfoZodObject.omit({
    _id: true,
    _type: true,
    _createdAt: true,
});

type FactoryParams = z.infer<typeof contactInfoFactoryParamsZodObject>;

const _contactInfoFromUserFactory = (
    userOrCustomer: User | Customer,
): ContactInfo => {
    if (isUserId(userOrCustomer._id)) {
        if (!userOrCustomer.email?.verified && !userOrCustomer.phone.verified) {
            throw createAppError({
                errorCode: "UNAUTHENTICATED",
                publicMessage:
                    "Either email or phone number must first be verified",
            });
        }
    }

    const emailAddress = userOrCustomer.email?.emailAddress;
    const phoneNumber = userOrCustomer.phone.phoneNumber;

    const contactInfo = contactInfoFactory({
        // _id: generateId({ prefix: "contact_info__" }),
        name: fullName({
            firstName: userOrCustomer?.firstName || "Kund",
            lastName: userOrCustomer?.lastName || "",
        }),
        firstName: userOrCustomer.firstName,
        lastName: userOrCustomer.lastName,
        emailAddress,
        phoneNumber,
        image: isUser(userOrCustomer) ? userOrCustomer.image || null : null,
        customerId: userOrCustomer._id,
    });

    return contactInfo;
};

function contactInfoFactory(params: FactoryParams): ContactInfo;
function contactInfoFactory(user: User | Customer): ContactInfo;
function contactInfoFactory(
    params: User | Customer | FactoryParams,
): ContactInfo {
    if (isUser(params) || isCustomer(params)) {
        return _contactInfoFromUserFactory(params);
    }
    return {
        ...params,
        _id: generateId({ prefix: "contact_info__" }),
        _type: "CONTACT_INFO",
        _createdAt: nowIsoAndUnix(),
        image: params.image || null,
    };
}

const anonymizedContactInfoFactory = (
    contactInfo: ContactInfo,
): ContactInfo => {
    return {
        ...contactInfo,
        _id: generateId({ prefix: "contact_info__" }),
        name: undefined,
        firstName: undefined,
        lastName: undefined,
        emailAddress: undefined,
        phoneNumber: defaultAnonymizedPhoneNumber,
        image: undefined,
        businessName: undefined,
        businessOrgNo: undefined,
        businessVatNo: undefined,
    };
};

const emptyContactInfo = () =>
    contactInfoFactory({
        phoneNumber: defaultAnonymizedPhoneNumber,
    });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isContactInfo = (value: any): value is ContactInfo => {
    return value._type === "CONTACT_INFO";
};

export {
    anonymizedContactInfoFactory,
    contactInfoFactory,
    contactInfoFactoryParamsZodObject,
    contactInfoZodObject,
    emptyContactInfo,
    isContactInfo,
};
export type { ContactInfo, FactoryParams as ContactInfoFactoryParams };
